import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  RiLoader4Line,
  RiCheckboxCircleLine,
  RiEditLine,
  RiAttachmentLine,
  RiTimeLine,
  RiEmotionSadLine,
  RiArrowLeftRightLine,
  RiDeleteBinLine
} from 'react-icons/ri';
import deviceMaintenanceService, { ActivityTimelineItem } from '../../services/deviceMaintenanceService';
import { DEVICE_CONDITIONS, REPLACEMENT_TYPES, SIM_CONDITIONS } from '../../services/deviceReplacementService';
import repairMethodService from '../../services/repairMethodService';
import maintenanceTypeService from '../../services/maintenanceTypeService';
import { queryKeys } from '../../queryClient';
import { formatDateTime } from '../../utils/dateUtils';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { statusColors } from '../../config/designSystem';
import { usePermission } from '../../hooks/usePermission';
import StatusBadge from './StatusBadge';
import TimelineAttachmentLink from './TimelineAttachmentLink';

// "มีสถานะจริงไหม" — backend ส่ง null มาได้ (เคสที่ระบบสร้างให้ ไม่เคยมีสถานะเดิม)
// และบาง payload เก่าส่งสตริง 'None' (str(None) ฝั่ง Python)
const hasStatus = (raw: any): boolean =>
  raw !== null && raw !== undefined && raw !== '' && raw !== 'None';

// FB-2026-0011: แถวประวัติสถานะ เดิม→เดิม = "บันทึกหมายเหตุ (สถานะคงเดิม)"
const isNoteRow = (activity: { details?: any }): boolean =>
  hasStatus(activity.details?.old_status) && activity.details?.old_status === activity.details?.new_status;

// แปลงสถานะซ่อมบำรุง (enum อังกฤษ) → ป้ายไทย — ใช้แหล่งเดียวกับ StatusBadge (single source of truth)
const maintenanceStatusLabel = (raw: any): string => {
  if (!hasStatus(raw)) return 'ไม่มี';
  const key = String(raw).toUpperCase();
  const cfg = (statusColors.maintenance as any)[key];
  return cfg?.label || String(raw);
};

// field ใน edit-history ที่เป็นวันที่-เวลา → แสดงรูปแบบไทยแทนค่าดิบ
const DATETIME_FIELDS = new Set(['maintenance_date', 'completed_date']);
// "2026-09-02 04:15:51.482267" (str(datetime) ของ Python, ไม่มี T + microseconds) → ISO ที่ทุก browser
// parse ได้ "2026-09-02T04:15:51.482" (Safari ไม่รับช่องว่าง / เกิน 3 หลักของเศษวินาที)
export const normalizeHistoryDateTime = (raw: string): string => {
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)(?:\.(\d+))?$/);
  if (!m) return raw;
  const ms = m[3] ? `.${m[3].slice(0, 3).padEnd(3, '0')}` : '';
  return `${m[1]}T${m[2]}${ms}`;
};

interface ActivityTimelineProps {
  maintenanceId: number;
  refreshTrigger?: number;
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ maintenanceId, refreshTrigger = 0 }) => {
  const { hasPermission } = usePermission();
  const [activities, setActivities] = useState<ActivityTimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // FK-label resolution: edit-history เก็บ "ค่าดิบ" ของ FK เป็น id (เช่น repair_method_id="18")
  // จึงต้อง map id → ชื่อ เพื่อให้ประวัติอ่านรู้เรื่อง (แก้ทั้งเรคคอร์ดเก่าและใหม่ ที่ display layer)
  // ใช้ master list ที่ cache อยู่แล้ว (แชร์ queryKey กับฟอร์มสร้าง/แก้ไข — ไม่ยิงซ้ำ)
  /**
   * สอง query นี้ใช้แปลง FK id → ชื่อ ในประวัติการแก้ไข **ไม่ใช่เนื้อหาหลักของ timeline**
   * แต่ยิงคนละ endpoint กับหน้านี้และ gate คนละสิทธิ์ (`repair_methods.view` /
   * `maintenance_types.view`) — route guard ของหน้าเช็คแค่ `maintenance.view`
   * → บทบาทบัญชี (มี maintenance.view แต่ไม่มีสองตัวนี้) ได้ 403 สองครั้งทุกครั้งที่เปิดเคส
   * ไม่มีสิทธิ์ = ไม่ยิง · `fkNameMaps` ว่าง → แสดงค่าดิบแทนชื่อ (เสียความสวย ไม่เสียข้อมูล)
   */
  const canViewRepairMethods = hasPermission('repair_methods.view');
  const canViewMaintenanceTypes = hasPermission('maintenance_types.view');
  const { data: repairMethods } = useQuery({
    queryKey: queryKeys.repairMethods.active(),
    queryFn: () => repairMethodService.getActiveRepairMethods(),
    staleTime: 5 * 60 * 1000,
    enabled: canViewRepairMethods,
  });
  const { data: maintenanceTypes } = useQuery({
    queryKey: queryKeys.maintenanceTypes.active(),
    queryFn: () => maintenanceTypeService.getActiveMaintenanceTypes(),
    staleTime: 5 * 60 * 1000,
    enabled: canViewMaintenanceTypes,
  });

  // registry: field ที่เป็น FK → ตาราง map id→ชื่อ (เพิ่ม field ใหม่ที่นี่ที่เดียว)
  const fkNameMaps: Record<string, Record<string, string>> = {
    repair_method_id: Object.fromEntries((repairMethods ?? []).map((r: any) => [String(r.id), r.name])),
    maintenance_type_id: Object.fromEntries((maintenanceTypes ?? []).map((t: any) => [String(t.id), t.name])),
  };

  // แปลงค่าใน edit-history ให้อ่านรู้เรื่อง: FK id→ชื่อ, ว่าง→"-", ชื่อ(ที่ backend อาจเก็บแล้ว)→คงเดิม,
  // field วันที่-เวลา → รูปแบบไทย (edit-history เก็บ str(datetime) ของ Python เช่น
  // "2026-09-02 04:15:51.482267" — E2E v4 OBS-B3 โชว์ดิบพร้อม microseconds)
  const resolveFieldValue = (fieldName: string, value: any): string => {
    if (value === null || value === undefined || value === '' || value === 'None') return '-';
    const raw = String(value);
    const map = fkNameMaps[fieldName];
    // map เฉพาะเมื่อค่าเป็น id ตัวเลขล้วน (กันกรณี backend เก็บชื่อมาแล้ว = ไม่ใช่ตัวเลข)
    if (map && /^\d+$/.test(raw)) {
      return map[raw] || `#${raw}`; // ถ้าไม่เจอ (ถูกลบ/inactive) แสดง #id แทนเลขดิบ
    }
    if (DATETIME_FIELDS.has(fieldName)) {
      const formatted = formatDateTime(normalizeHistoryDateTime(raw));
      return formatted === '-' ? raw : formatted;
    }
    return raw;
  };

  useEffect(() => {
    loadActivities();
  }, [maintenanceId, refreshTrigger]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await deviceMaintenanceService.getActivityTimeline(maintenanceId);
      setActivities(data);
    } catch (error: any) {
      console.error('Error loading activities:', error);

      // BUG-S170-STATUSMSG (ตระกูลเดียวกับ BUG-NOTI-DEADREF-RAWERR): error envelope ของ backend
      // ซ่อนข้อความไทยไว้ที่ data.error.user_message — อ่าน data.message ตรงๆ จะได้ undefined
      // แล้วตกไปที่ error.message ของ axios = "Request failed with status code 409" ขึ้นจอผู้ใช้
      // P-22 (E2E v5 maintenance B-28): เปลี่ยนมาใช้ helper กลาง getApiErrorMessage
      const errorMessage = getApiErrorMessage(error, 'ไม่สามารถโหลดประวัติกิจกรรมได้');

      setError(errorMessage);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'status_change':
        return (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-info-100 ring-8 ring-white">
            <RiCheckboxCircleLine className="h-5 w-5 text-info-600" />
          </div>
        );
      case 'edit':
        return (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning-100 ring-8 ring-white">
            <RiEditLine className="h-5 w-5 text-warning-600" />
          </div>
        );
      case 'attachment':
        return (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success-100 ring-8 ring-white">
            <RiAttachmentLine className="h-5 w-5 text-success-600" />
          </div>
        );
      case 'attachment_deleted':
        return (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-danger-100 ring-8 ring-white">
            <RiDeleteBinLine className="h-5 w-5 text-danger-600" />
          </div>
        );
      case 'created':
        return (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 ring-8 ring-white">
            <RiTimeLine className="h-5 w-5 text-accent-600" />
          </div>
        );
      case 'replacement':
        return (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 ring-8 ring-white">
            <RiArrowLeftRightLine className="h-5 w-5 text-primary-600" />
          </div>
        );
      // เคสจากแอพภายนอก: รับเคส / ระบบผูกอุปกรณ์อัตโนมัติจากโครงการ (PO 2026-09-06)
      case 'external_received':
        return (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 ring-8 ring-white">
            <RiTimeLine className="h-5 w-5 text-primary-600" />
          </div>
        );
      case 'auto_link':
        return (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 ring-8 ring-white">
            <RiCheckboxCircleLine className="h-5 w-5 text-amber-600" />
          </div>
        );
      default:
        return (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 ring-8 ring-white">
            <RiTimeLine className="h-5 w-5 text-secondary-600" />
          </div>
        );
    }
  };

  const renderActivityDetails = (activity: ActivityTimelineItem) => {
    if (!activity.details) return null;

    switch (activity.type) {
      case 'status_change': {
        // เคสที่ระบบสร้างให้อัตโนมัติ (เช่น จากการบันทึกเปลี่ยนอุปกรณ์) ไม่มีสถานะเดิมเสมอ —
        // เคสเกิดมาก็ปิดเลย ไม่เคยถูกเปิดค้าง · เดิมใส่ `|| 'unknown'` ซึ่ง StatusBadge ตกลงมาที่
        // 'OPEN' (StatusBadge.tsx:44 fallback) → ป้ายโชว์ "เปิด → ปิด" เหมือนมีคนเปิดเคสค้างไว้
        // แล้วมาปิดทีหลัง ขัดกับบรรทัดหัวข้อข้าง ๆ ที่เขียนถูกว่า เปลี่ยนสถานะจาก "ไม่มี" เป็น "ปิด"
        const oldStatus = activity.details.old_status;
        const newStatus = activity.details.new_status;
        if (!hasStatus(newStatus)) return null;
        if (isNoteRow(activity)) {
          return (
            <div className="mt-2 text-sm" data-testid="timeline-status-note">
              <StatusBadge status={String(newStatus)} />
              {activity.details.reason && (
                <p className="mt-1 text-secondary-700 whitespace-pre-line break-words">{activity.details.reason}</p>
              )}
            </div>
          );
        }
        return (
          <div className="mt-2 flex items-center gap-2 text-sm">
            {hasStatus(oldStatus) ? (
              <>
                <StatusBadge status={String(oldStatus)} />
                <span className="text-gray-400">→</span>
              </>
            ) : (
              <span className="text-xs text-secondary-500">สร้างเคสพร้อมสถานะ</span>
            )}
            <StatusBadge status={String(newStatus)} />
            {activity.details.reason && (
              <span className="ml-2 text-xs text-gray-500">({activity.details.reason})</span>
            )}
          </div>
        );
      }
      case 'edit':
        // Handle multiple field changes from backend: { field_name: { old, new } }
        const fieldChanges = Object.entries(activity.details || {}).filter(
          ([key]) => key !== 'field_name' && key !== 'old_value' && key !== 'new_value'
        );

        // Check for old format: { field_name, old_value, new_value }
        if (activity.details?.field_name) {
          return (
            <div className="mt-2 text-sm text-secondary-600">
              <span className="font-medium">{activity.details.field_name}</span>:
              <span className="ml-1 text-danger-700 line-through">{activity.details.old_value}</span>
              <span className="mx-1">→</span>
              <span className="text-success-700">{activity.details.new_value}</span>
            </div>
          );
        }

        // New format: { field_name: { old, new } }
        if (fieldChanges.length > 0) {
          const fieldLabels: Record<string, string> = {
            'details': 'รายละเอียด',
            'result': 'ผลลัพธ์',
            'status': 'สถานะ',
            'maintenance_type_id': 'ประเภทการซ่อมบำรุง',
            'repair_method_id': 'วิธีการแก้ไข',
            'performed_by': 'ผู้ดำเนินการ',
            'maintenance_date': 'วันที่ซ่อมบำรุง',
            'completed_date': 'วันที่เสร็จสิ้น',
            'contact_name': 'ผู้ติดต่อ',
            'contact_phone': 'เบอร์โทร',
            // สลับเครื่องของเคสจากการ์ด "อุปกรณ์ทั้งหมดของโครงการนี้" (เคส #537, 23 ก.ย. 2026)
            // backend (label_fk_changes) แปลง id ดิบเป็นป้ายอ่านออกให้แล้ว (เช่น "337-V005 · SN…")
            // resolveFieldValue ปล่อยผ่านเพราะไม่ใช่ตัวเลขล้วน — ที่นี่แค่ตั้งชื่อ field ภาษาไทย
            'device_id': 'อุปกรณ์',
            'rental_id': 'สัญญาเช่า',
            'site_id': 'โครงการ',
            'company_id': 'บริษัท'
          };

          return (
            <div className="mt-2 space-y-1">
              {fieldChanges.map(([fieldName, change]: [string, any]) => {
                if (!change || typeof change !== 'object') return null;
                const label = fieldLabels[fieldName] || fieldName;
                const oldVal = resolveFieldValue(fieldName, change.old);
                const newVal = resolveFieldValue(fieldName, change.new);

                // Truncate long values
                const truncate = (s: string, max: number = 50) =>
                  s && s.length > max ? s.substring(0, max) + '...' : s;

                return (
                  <div key={fieldName} className="text-sm text-secondary-600">
                    <span className="font-medium">{label}</span>:
                    <span className="ml-1 text-danger-700 line-through" title={String(oldVal)}>
                      {truncate(String(oldVal))}
                    </span>
                    <span className="mx-1">→</span>
                    <span className="text-success-700" title={String(newVal)}>
                      {truncate(String(newVal))}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        }

        return null;
      case 'attachment':
        return (
          <div className="mt-2 text-sm text-secondary-600">
            <span className="font-medium">ไฟล์:</span>
            {/* กดเปิดดู/ดาวน์โหลดได้ (feedback 29 ก.ย. 2026) — activity.id ของแถว attachment = id ไฟล์แนบ */}
            <TimelineAttachmentLink
              maintenanceId={maintenanceId}
              attachmentId={activity.id}
              fileName={activity.details.file_name}
              fileType={activity.details.file_type}
            />
            {activity.details.file_size && (
              <span className="ml-2 text-xs text-secondary-500">
                ({(activity.details.file_size / 1024).toFixed(1)} KB)
              </span>
            )}
          </div>
        );
      case 'attachment_deleted':
        // BUG-S2295-002: ไฟล์ที่ถูกลบหายจากรายการ "อัปโหลดไฟล์" — แถวนี้คือร่องรอยเดียวบนไทม์ไลน์ว่าเคยแนบ + ใครลบ
        return (
          <div className="mt-2 text-sm text-secondary-600" data-testid="attachment-deleted-details">
            <span className="font-medium">ไฟล์:</span>
            <span className="ml-1 line-through">{activity.details?.file_name}</span>
            {activity.details?.uploaded_by && (
              <span className="ml-2 text-xs text-secondary-500">
                (อัปโหลดโดย {activity.details.uploaded_by}
                {activity.details.uploaded_at ? ` · ${formatDateTime(activity.details.uploaded_at)}` : ''})
              </span>
            )}
          </div>
        );
      case 'replacement': {
        // การเปลี่ยนอุปกรณ์/SIM ที่ผูกกับเคส — โชว์ เก่า → ใหม่ + สภาพของเดิม + เหตุผล
        // (user feedback 2026-08-31 เคส #254: "หากมีการเปลี่ยน device ควรเห็นใน timeline ประวัติด้วย")
        const d = activity.details;
        const showDevice = d.replacement_type === 'DEVICE_ONLY' || d.replacement_type === 'BOTH';
        const showSim = d.replacement_type === 'SIM_ONLY' || d.replacement_type === 'BOTH';
        const deviceLabel = (x: any) =>
          x ? [x.code, x.serial_number].filter(Boolean).join(' · ') || `#${x.id}` : '-';
        const simLabel = (x: any) => (x ? x.phone_number || `#${x.id}` : '-');
        const deviceCond = DEVICE_CONDITIONS.find((c) => c.value === d.old_device_condition)?.label;
        const simCond = SIM_CONDITIONS.find((c) => c.value === d.old_sim_condition)?.label;
        return (
          <div className="mt-2 space-y-1 text-sm text-secondary-600" data-testid="replacement-details">
            {showDevice && (
              <div>
                <span className="font-medium">อุปกรณ์:</span>
                <span className="ml-1 text-danger-700 line-through">{deviceLabel(d.old_device)}</span>
                <span className="mx-1">→</span>
                <span className="text-success-700">{deviceLabel(d.new_device)}</span>
                {deviceCond && <span className="ml-2 text-xs text-secondary-500">(เครื่องเดิม: {deviceCond})</span>}
              </div>
            )}
            {showSim && (
              <div>
                <span className="font-medium">SIM:</span>
                <span className="ml-1 text-danger-700 line-through">{simLabel(d.old_sim)}</span>
                <span className="mx-1">→</span>
                <span className="text-success-700">{simLabel(d.new_sim)}</span>
                {simCond && <span className="ml-2 text-xs text-secondary-500">(SIM เดิม: {simCond})</span>}
              </div>
            )}
            {/* AnyDesk ใช้หน้าตาเดียวกับบรรทัดอุปกรณ์/SIM — ป้ายหนา · ค่าเดิมขีดฆ่าสีแดง · ค่าใหม่สีเขียว · ขนาดเท่ากัน
                (feedback suraluck 29 ก.ย. 2026 เคส #597: เดิมเป็นตัวเล็กสีเทาไม่มีขีด อ่านไม่ออกว่าอันไหนเก่า/ใหม่)
                ไม่มีค่าเดิม → "-" ธรรมดา ไม่ขีดฆ่าเครื่องหมายขีด */}
            {(d.old_anydesk_id || d.new_anydesk_id) && (
              <div data-testid="replacement-anydesk">
                <span className="font-medium">AnyDesk:</span>
                {d.old_anydesk_id ? (
                  <span className="ml-1 text-danger-700 line-through">{d.old_anydesk_id}</span>
                ) : (
                  <span className="ml-1">-</span>
                )}
                <span className="mx-1">→</span>
                <span className="text-success-700">{d.new_anydesk_id || '-'}</span>
              </div>
            )}
            {d.reason && <div className="text-xs text-secondary-500 break-words">เหตุผล: {d.reason}</div>}
          </div>
        );
      }
      default:
        return null;
    }
  };

  // หัวข้อของรายการ: status_change/replacement แปลเป็นไทยที่ display layer, ที่เหลือใช้ description จาก backend
  const activityHeadline = (activity: ActivityTimelineItem): string => {
    if (activity.type === 'status_change') {
      // FB-2026-0011: แถวเดิม→เดิม = หมายเหตุที่บันทึกโดยคงสถานะเดิม ไม่ใช่การเปลี่ยนสถานะ
      if (isNoteRow(activity)) {
        return `บันทึกหมายเหตุ (สถานะคงเดิม "${maintenanceStatusLabel(activity.details?.new_status)}")`;
      }
      return `เปลี่ยนสถานะจาก "${maintenanceStatusLabel(activity.details?.old_status)}" เป็น "${maintenanceStatusLabel(activity.details?.new_status)}"`;
    }
    if (activity.type === 'replacement') {
      const t = REPLACEMENT_TYPES.find((x) => x.value === activity.details?.replacement_type);
      return t ? `${t.label} (จากเคสซ่อมนี้)` : activity.description;
    }
    return activity.description;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RiLoader4Line className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="ml-2 text-sm text-secondary-500">กำลังโหลดประวัติกิจกรรม...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg">
        <p className="text-sm text-danger-800">{error}</p>
        <button
          type="button"
          onClick={loadActivities}
          className="mt-2 text-sm text-danger-600 hover:text-danger-700 underline"
        >
          ลองอีกครั้ง
        </button>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <RiEmotionSadLine className="mx-auto h-12 w-12 text-secondary-300" />
        <p className="mt-2 text-sm text-secondary-500">ยังไม่มีประวัติกิจกรรม</p>
      </div>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {activities.map((activity, activityIdx) => (
          // id ซ้ำข้ามตารางได้ (status_history #5 กับ attachment #5) → key ต้องรวม type กัน React key ชน
          <li key={`${activity.type}-${activity.id}`}>
            <div className="relative pb-8">
              {activityIdx !== activities.length - 1 ? (
                <span
                  className="absolute left-5 top-5 -ml-px h-full w-0.5 bg-secondary-200"
                  aria-hidden="true"
                />
              ) : null}
              <div className="relative flex items-start space-x-3">
                {/* Icon */}
                <div className="relative">{getActivityIcon(activity.type)}</div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div>
                    <div className="text-sm">
                      <span className="font-medium text-secondary-900">{activity.user}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-secondary-500">{activityHeadline(activity)}</p>
                  </div>

                  {/* Details */}
                  {renderActivityDetails(activity)}

                  {/* Timestamp */}
                  <div className="mt-2 text-xs text-secondary-600">
                    {formatDateTime(activity.timestamp)}
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ActivityTimeline;
